import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
forwardRef,
} from 'react';

import './ExplanationLesson.css';

const SWIPE_THRESHOLD = 55;
const ANIMATION_DURATION = 240;

const getExplanationItemHtml = (
  explanationItem
) => {
  if (
    typeof explanationItem === 'string'
  ) {
    return explanationItem;
  }

  if (
    !explanationItem ||
    typeof explanationItem !== 'object'
  ) {
    return '';
  }

  return (
    explanationItem.html ||
    explanationItem.description ||
    explanationItem.descriptionExplanation ||
    explanationItem.text ||
    ''
  );
};

const ExplanationLesson = forwardRef(({
  question,
  cardIndex = 0,
  onCardIndexChange,
  onUnlockContinue,
}, ref) => {
  const descriptions = Array.isArray(
    question?.descriptionExplanation
  )
    ? question.descriptionExplanation
    : [];

  const totalCards = descriptions.length;

  const [
    leavingCardIndex,
    setLeavingCardIndex,
  ] = useState(null);

  const [
    swipeDirection,
    setSwipeDirection,
  ] = useState('left');

  const [
    isAnimating,
    setIsAnimating,
  ] = useState(false);

  const [
    dragOffset,
    setDragOffset,
  ] = useState({
    x: 0,
    y: 0,
  });

  const animationTimerRef =
    useRef(null);

  const pointerStateRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    horizontal: false,
  });

  useEffect(() => {
    setLeavingCardIndex(null);
    setSwipeDirection('left');
    setIsAnimating(false);

    setDragOffset({
      x: 0,
      y: 0,
    });

    pointerStateRef.current = {
      active: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
      horizontal: false,
    };

    if (
      animationTimerRef.current
    ) {
      clearTimeout(
        animationTimerRef.current
      );

      animationTimerRef.current =
        null;
    }

    onCardIndexChange?.(0);
  }, [
    question,
    onCardIndexChange,
  ]);

  useEffect(() => {
    if (totalCards <= 1) {
      onUnlockContinue?.();
    }
  }, [
    totalCards,
    onUnlockContinue,
  ]);

  useEffect(() => {
    if (
      totalCards > 0 &&
      cardIndex === totalCards - 1
    ) {
      onUnlockContinue?.();
    }
  }, [
    cardIndex,
    totalCards,
    onUnlockContinue,
  ]);

  useEffect(() => {
    return () => {
      if (
        animationTimerRef.current
      ) {
        clearTimeout(
          animationTimerRef.current
        );
      }
    };
  }, []);

  const resetPointerState =
    useCallback(() => {
      pointerStateRef.current = {
        active: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        lastX: 0,
        lastY: 0,
        horizontal: false,
      };
    }, []);

  const animateToCard = useCallback(
    (direction) => {
      if (
        isAnimating ||
        totalCards <= 1
      ) {
        resetPointerState();
        return;
      }

      const nextIndex =
        direction === 'left'
          ? (
              cardIndex + 1
            ) % totalCards
          : (
              cardIndex -
              1 +
              totalCards
            ) % totalCards;

      setIsAnimating(true);
      setSwipeDirection(direction);
      setLeavingCardIndex(cardIndex);

      setDragOffset({
        x:
          direction === 'left'
            ? -window.innerWidth
            : window.innerWidth,
        y: 0,
      });

      animationTimerRef.current =
        setTimeout(() => {
          onCardIndexChange?.(
            nextIndex
          );

          setLeavingCardIndex(null);
          setIsAnimating(false);

          setDragOffset({
            x: 0,
            y: 0,
          });

          animationTimerRef.current =
            null;
        }, ANIMATION_DURATION);
    },
    [
      cardIndex,
      isAnimating,
      onCardIndexChange,
      resetPointerState,
      totalCards,
    ]
  );

  useImperativeHandle(
  ref,
  () => ({
    nextCard: () => {
      animateToCard('left');
    },

    previousCard: () => {
      animateToCard('right');
    },
  }),
  [animateToCard]
);

  const handlePointerDown =
    useCallback(
      (event) => {
        if (isAnimating) {
          return;
        }

        if (
          event.pointerType ===
            'mouse' &&
          event.button !== 0
        ) {
          return;
        }

        pointerStateRef.current = {
          active: true,
          pointerId:
            event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastY: event.clientY,
          horizontal: false,
        };

        event.currentTarget
          ?.setPointerCapture?.(
            event.pointerId
          );
      },
      [isAnimating]
    );

  const handlePointerMove =
    useCallback(
      (event) => {
        const state =
          pointerStateRef.current;

        if (
          !state.active ||
          state.pointerId !==
            event.pointerId ||
          isAnimating
        ) {
          return;
        }

        const deltaX =
          event.clientX -
          state.startX;

        const deltaY =
          event.clientY -
          state.startY;

        state.lastX =
          event.clientX;

        state.lastY =
          event.clientY;

        if (
          !state.horizontal &&
          (
            Math.abs(deltaX) > 8 ||
            Math.abs(deltaY) > 8
          )
        ) {
          state.horizontal =
            Math.abs(deltaX) >
            Math.abs(deltaY);
        }

        if (!state.horizontal) {
          return;
        }

        event.preventDefault();

        setDragOffset({
          x: deltaX,
          y: deltaY * 0.08,
        });
      },
      [
        isAnimating,
      ]
    );

  const handlePointerUp =
    useCallback(
      (event) => {
        const state =
          pointerStateRef.current;

        if (
          !state.active ||
          state.pointerId !==
            event.pointerId
        ) {
          return;
        }

        const deltaX =
          state.lastX -
          state.startX;

        const direction =
          deltaX < 0
            ? 'left'
            : 'right';

        try {
          event.currentTarget
            ?.releasePointerCapture?.(
              event.pointerId
            );
        } catch {
          // pointer-ul poate fi deja eliberat
        }

        resetPointerState();

        if (
          state.horizontal &&
          Math.abs(deltaX) >=
            SWIPE_THRESHOLD
        ) {
          animateToCard(
            direction
          );
        } else {
          setDragOffset({
            x: 0,
            y: 0,
          });
        }
      },
      [
        animateToCard,
        resetPointerState,
      ]
    );

  const handlePointerCancel =
    useCallback(() => {
      resetPointerState();

      setDragOffset({
        x: 0,
        y: 0,
      });
    }, [
      resetPointerState,
    ]);

  return (
    <section className="explanation-lesson">
      {question.image && (
        <img
          src={question.image}
          alt={
            question
              .titleExplanation ||
            'Imagine explicativa'
          }
          className="explanation-lesson__image"
        />
      )}

      {totalCards === 0 ? (
        <div className="explanation-lesson__empty">
          Continutul explicatiei nu este disponibil.
        </div>
      ) : (
        <div
          className="explanation-lesson__card-stack"
          onPointerDown={
            handlePointerDown
          }
          onPointerMove={
            handlePointerMove
          }
          onPointerUp={
            handlePointerUp
          }
          onPointerCancel={
            handlePointerCancel
          }
        >
          {descriptions.map(
            (
              explanation,
              index
            ) => {
              const offset =
                (
                  index -
                  cardIndex +
                  totalCards
                ) % totalCards;

              const isLeaving =
                index ===
                leavingCardIndex;

              if (
                offset > 3 &&
                !isLeaving
              ) {
                return null;
              }

              const isActive =
                offset === 0 &&
                !isLeaving;

              const isNext =
                offset === 1 &&
                !isLeaving;

              const explanationHtml =
                getExplanationItemHtml(
                  explanation
                );

              let transform =
                'translate3d(0, 0, 0)';

              let opacity = 1;

              if (isLeaving) {
                const exitX =
                  swipeDirection ===
                  'left'
                    ? '-115%'
                    : '115%';

                transform = `
                  translate3d(
                    ${exitX},
                    0,
                    0
                  )
                  rotate(
                    ${
                      swipeDirection ===
                      'left'
                        ? '-6deg'
                        : '6deg'
                    }
                  )
                  scale(0.97)
                `;

                opacity = 0;
              }

              else if (isActive) {
                const rotation =
                  Math.max(
                    -8,
                    Math.min(
                      8,
                      dragOffset.x / 45
                    )
                  );

                const dragScale =
                  Math.max(
                    0.965,
                    1 -
                      Math.abs(
                        dragOffset.x
                      ) / 5000
                  );

                transform = `
                  translate3d(
                    ${dragOffset.x}px,
                    ${dragOffset.y}px,
                    0
                  )
                  rotate(
                    ${rotation}deg
                  )
                  scale(
                    ${dragScale}
                  )
                `;

                opacity =
                  Math.max(
                    0.72,
                    1 -
                      Math.abs(
                        dragOffset.x
                      ) / 700
                  );
              }

              else {
                const progress =
                  Math.min(
                    Math.abs(
                      dragOffset.x
                    ) /
                      SWIPE_THRESHOLD,
                    1
                  );

                const rotations = [
                  0,
                  -2.2,
                  2.8,
                  -3.8,
                ];

                const translateX = [
                  0,
                  -7,
                  8,
                  -10,
                ];

                const translateY = [
                  0,
                  12,
                  22,
                  32,
                ];

                const scaleValues = [
                  1,
                  0.985,
                  0.97,
                  0.955,
                ];

                const baseRotation =
                  rotations[offset] ?? 0;

                const baseX =
                  translateX[offset] ?? 0;

                const baseY =
                  translateY[offset] ?? 0;

                const baseScale =
                  scaleValues[offset] ??
                  0.95;

                const animatedY =
                  baseY -
                  progress *
                    Math.min(
                      baseY,
                      10
                    );

                const animatedScale =
                  baseScale +
                  progress *
                    (
                      1 -
                      baseScale
                    ) *
                    0.5;

                transform = `
                  translate3d(
                    ${baseX}px,
                    ${animatedY}px,
                    0
                  )
                  rotate(
                    ${baseRotation}deg
                  )
                  scale(
                    ${animatedScale}
                  )
                `;

                opacity =
                  Math.max(
                    0.76,
                    1 -
                      offset *
                        0.08
                  );
              }

              return (
                <article
                  key={index}
                  className={[
                    'explanation-lesson__card',

                    isActive
                      ? 'explanation-lesson__card--active'
                      : '',

                    isActive &&
                    dragOffset.x !== 0
                      ? 'explanation-lesson__card--dragging'
                      : '',

                    isNext
                      ? 'explanation-lesson__card--next'
                      : '',

                    isLeaving
                      ? 'explanation-lesson__card--leaving'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    transform,
                    opacity,

                    zIndex:
                      isLeaving
                        ? 10
                        : totalCards -
                          offset,
                  }}
                >
                  <div
                    className="explanation-lesson__text"
                    dangerouslySetInnerHTML={{
                      __html:
                        explanationHtml,
                    }}
                  />
                </article>
              );
            }
          )}
        </div>
      )}
    </section>
  );
});

export default ExplanationLesson;